import { makeStyles } from "@material-ui/core/styles";
import { ClassNameMap } from "@material-ui/core/styles/withStyles";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Page } from ".";
import { useScreenSize } from "../../../common/hooks/useScreenSize";
import { spaceBetween } from "../../../common/util/Array/spaceBetween";
import { LazyLoad } from "../../../common/util/LazyLoad";
import { sFetchJson } from "../../../common/util/sFetchJson";
import { SeasonAnimation } from "../../shared/Animations/SeasonAnimation";
import ShurikenProgress from "../../shared/Animations/ShurikenProgress";
import { AuthorArea } from "../../shared/Author/Author";
import { Author } from "../../shared/Author/types";
import CharacterComment from "../../shared/CharacterComment";
import FB from "../../shared/FaceBook";
import Helmet from "../../shared/Helmet";
import { ArticleScroll, ArticlesListProps } from "./ArticleScroll";

const initialArticlesNumber = 10;

export const pageTitle = "Articles about Japan";
export const desc =
    "Articles about studying Japanese language and culture! I hope these articles help you to learn about Japan!";
const imgNumber = 1;

export default function ArticlesTop() {
    const c = useTopStyles();
    const { screenWidth } = useScreenSize();

    const [articles, setArticles] = useState<Page[]>([]);
    const [allAuthors, setAllAuthors] = useState<Author[]>([]);

    useEffect(() => {
        fetchArticles(setArticles, setAllAuthors);
    }, []);

    return (
        <div className={c.outsideContainer}>
            <Helmet title={pageTitle} desc={desc} />

            <div className={c.main}>
                <BreadCrumbs c={c} />
                <h1
                    className={spaceBetween("whiteShadow", c.h1)}
                    style={{
                        margin: screenWidth > 600 ? "40px 0" : "30px 0 40px",
                    }}
                >
                    {pageTitle}
                </h1>
                <CharacterComment
                    imgNumber={imgNumber}
                    screenWidth={screenWidth}
                    comment={
                        <div className={c.characterComment}>
                            {desc.split("! ").map((d, i, arr) => (
                                <p key={i}>
                                    {d + (i < arr.length - 1 ? "! " : "")}
                                </p>
                            ))}
                        </div>
                    }
                    commentStyle={{
                        paddingLeft: 20,
                        paddingRight: 20,
                    }}
                />
                <div className={c.container}>
                    <ArticlesList
                        titleH={"h2"}
                        articles={articles}
                        screenWidth={screenWidth}
                        allAuthors={allAuthors}
                    />
                    <AuthorArea
                        style={{ marginTop: 45 }}
                        screenWidth={screenWidth}
                        author={allAuthors.find(a => a.authorId === 1)}
                    />
                </div>
                <FB
                    style={{
                        marginTop: 20,
                        width: "100%",
                        textAlign: "center",
                    }}
                />
            </div>
            <SeasonAnimation frequencySec={2} screenWidth={screenWidth} />
        </div>
    );
}

const maxWidth = 900;
const useTopStyles = makeStyles({
    outsideContainer: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    main: { maxWidth, textAlign: "left" },
    h1: {
        fontWeight: "bolder",
        textAlign: "center",
    },
    characterComment: { textAlign: "left" },
    container: { margin: "20px 0" },
    red: { color: "red" },
    breadArrow: { marginLeft: 5, marginRight: 5 },
});

function BreadCrumbs({ c }: { c: ClassNameMap<"breadArrow"> }) {
    return (
        <div className="whiteShadow">
            <Link to={"/"}>Home</Link>
            <span className={c.breadArrow}>{" > "}</span>
            <span>Articles</span>
        </div>
    );
}

async function fetchArticles(
    setArticles: (articles: Page[]) => void,
    setAuthors: (authors: Author[]) => void
) {
    const url = "api/Articles/GetAllArticles";
    const articles = await sFetchJson<Page[]>(url);

    setArticles(articles);

    const allAuthors = await sFetchJson<Author[]>("api/Articles/GetAllAuthors");
    setAuthors(allAuthors);
}

export function ArticlesList({
    articles,
    screenWidth,
    titleH,
    allAuthors,
}: ArticlesListProps) {
    if (articles.length <= 0) {
        return <ShurikenProgress size="20%" />;
    }

    const isWide = screenWidth > 767;

    const imgSize = isWide
        ? {
              width: "100%",
              height: "100%",
          }
        : { width: "100%" };

    return (
        <>
            {articles.map((page, index) => {
                const articleScroll = (
                    <ArticleScroll
                        page={page}
                        isWide={isWide}
                        imgSize={imgSize}
                        titleH={titleH}
                        allAuthors={allAuthors}
                        screenWidth={screenWidth}
                        key={page.url}
                    />
                );

                if (index < initialArticlesNumber) {
                    return articleScroll;
                }
                return <LazyLoad key={page.url}>{articleScroll}</LazyLoad>;
            })}
        </>
    );
}
